import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Rankings from "@/pages/Rankings";
import WeeklyEntry from "@/pages/WeeklyEntry";
import PlayersAdmin from "@/pages/PlayersAdmin";
import TeamsAdmin from "@/pages/TeamsAdmin";
import UsersAdmin from "@/pages/UsersAdmin";
import CoachesAdmin from "@/pages/CoachesAdmin";
import StaffAdmin from "@/pages/StaffAdmin";
import Directory from "@/pages/Directory";
import MyProfile from "@/pages/MyProfile";
import Rules from "@/pages/Rules";
import Settings from "@/pages/Settings";
import Apply from "@/pages/Apply";
import Roster from "@/pages/Roster";
import Composition from "@/pages/Composition";
import ClubMembers from "@/pages/ClubMembers";

function MyClubRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.club ? `/composition/${encodeURIComponent(user.club)}` : "/composition"} replace />;
}
import WeeklySessions from "@/pages/WeeklySessions";
import SessionsMap from "@/pages/SessionsMap";
import RoomBooking from "@/pages/RoomBooking";
import DutyRoster from "@/pages/DutyRoster";
import PublicCheckin from "@/pages/PublicCheckin";
import PublicElection from "@/pages/PublicElection";
import PublicRecruit from "@/pages/PublicRecruit";
import PublicRecruitInvite from "@/pages/PublicRecruitInvite";
import PublicStudy from "@/pages/PublicStudy";
import StudyLibrary from "@/pages/StudyLibrary";
import Communiques from "@/pages/Communiques";
import PublicApply from "@/pages/PublicApply";
import MbtiTest from "@/pages/MbtiTest";
import Eureka from "@/pages/Eureka";
import Selection from "@/pages/Selection";
import ApplicationsAdmin from "@/pages/ApplicationsAdmin";
import MikeChats from "@/pages/MikeChats";
import Convocations from "@/pages/Convocations";
import CoachAttendance from "@/pages/CoachAttendance";
import CaptainAttendance from "@/pages/CaptainAttendance";
import Training from "@/pages/Training";
import SelfReport from "@/pages/SelfReport";
import MyPerformance from "@/pages/MyPerformance";
import HallOfFame from "@/pages/HallOfFame";
import Recruitment from "@/pages/Recruitment";
import Voice from "@/pages/Voice";
import VoiceProfile from "@/pages/VoiceProfile";
import AuthCallback from "@/pages/AuthCallback";
import MyConsultants from "@/pages/MyConsultants";
import PlatformsAssign from "@/pages/PlatformsAssign";

// Stable role arrays — defined at module scope so ProtectedRoute receives a stable
// reference and doesn't re-render on every App render.
const ROLES_CONSULTANT_ADMIN = ["consultant", "admin"];
const ROLES_COACH_ADMIN = ["coach", "admin"];
const ROLES_ADMIN_COACH_CONSULTANT = ["admin", "coach", "consultant", "staff"];

function App() {
  // Capture ?ref= from URL BEFORE any auth redirect happens. The OnboardingWizard
  // reads sessionStorage.kwgl_invite_ref later (after login) to pre-fill the recruiter.
  // Without this top-level capture, an unauthenticated joiner clicking the magic link
  // would lose the ref during the redirect to /login.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const ref = url.searchParams.get("ref");
      if (ref) {
        sessionStorage.setItem("kwgl_invite_ref", ref);
        url.searchParams.delete("ref");
        window.history.replaceState({}, "", url.toString());
      }
    } catch { /* SSR or private mode — safe to ignore */ }
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/voice" element={<Voice />} />
          <Route path="/candidatura" element={<PublicApply />} />
          <Route path="/presenca/:token" element={<PublicCheckin />} />
          <Route path="/eleicao/:token" element={<PublicElection />} />
          <Route path="/recrutamento/:token" element={<PublicRecruit />} />
          <Route path="/convite-clube/:token" element={<PublicRecruitInvite />} />
          <Route path="/estudo/:token" element={<PublicStudy />} />
          <Route path="/teste-mbti" element={<MbtiTest />} />
          <Route path="/voice/:id" element={<VoiceProfile />} />

          <Route path="/" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
          <Route path="/rankings" element={<ProtectedRoute><Layout><Rankings /></Layout></ProtectedRoute>} />
          <Route path="/hall-of-fame" element={<ProtectedRoute><Layout><HallOfFame /></Layout></ProtectedRoute>} />
          <Route path="/recruitment" element={<ProtectedRoute><Layout><Recruitment /></Layout></ProtectedRoute>} />
          <Route path="/rules" element={<ProtectedRoute><Layout><Rules /></Layout></ProtectedRoute>} />
          <Route path="/biblioteca" element={<ProtectedRoute><Layout><StudyLibrary /></Layout></ProtectedRoute>} />
          <Route path="/eureka" element={<ProtectedRoute><Layout><Eureka /></Layout></ProtectedRoute>} />
          <Route path="/selection" element={<ProtectedRoute><Layout><Selection /></Layout></ProtectedRoute>} />
          <Route path="/my-profile" element={<ProtectedRoute roles={ROLES_ADMIN_COACH_CONSULTANT}><Layout><MyProfile /></Layout></ProtectedRoute>} />

          <Route path="/coach" element={<ProtectedRoute roles={ROLES_COACH_ADMIN}><Layout><CoachAttendance /></Layout></ProtectedRoute>} />
          <Route path="/my-consultants" element={<ProtectedRoute roles={ROLES_COACH_ADMIN}><Layout><MyConsultants /></Layout></ProtectedRoute>} />
          <Route path="/platforms-assign" element={<ProtectedRoute roles={["admin", "staff"]}><Layout><PlatformsAssign /></Layout></ProtectedRoute>} />
          <Route path="/captain" element={<ProtectedRoute roles={ROLES_CONSULTANT_ADMIN}><Layout><CaptainAttendance /></Layout></ProtectedRoute>} />
          <Route path="/training" element={<ProtectedRoute roles={ROLES_ADMIN_COACH_CONSULTANT}><Layout><Training /></Layout></ProtectedRoute>} />
          <Route path="/self-report" element={<ProtectedRoute roles={ROLES_CONSULTANT_ADMIN}><Layout><SelfReport /></Layout></ProtectedRoute>} />
          <Route path="/o-meu-desempenho" element={<ProtectedRoute roles={ROLES_CONSULTANT_ADMIN}><Layout><MyPerformance /></Layout></ProtectedRoute>} />

          <Route path="/weekly-entry" element={<ProtectedRoute adminOnly><Layout><WeeklyEntry /></Layout></ProtectedRoute>} />
          <Route path="/players" element={<ProtectedRoute adminOnly><Layout><PlayersAdmin /></Layout></ProtectedRoute>} />
          <Route path="/assistants" element={<ProtectedRoute adminOnly><Layout><PlayersAdmin defaultRole="assistant" pageTitle="Assistentes" /></Layout></ProtectedRoute>} />
          <Route path="/teams" element={<ProtectedRoute adminOnly><Layout><TeamsAdmin /></Layout></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute adminOnly><Layout><UsersAdmin /></Layout></ProtectedRoute>} />
          <Route path="/coaches" element={<ProtectedRoute adminOnly><Layout><CoachesAdmin /></Layout></ProtectedRoute>} />
          <Route path="/staff" element={<ProtectedRoute adminOnly><Layout><StaffAdmin /></Layout></ProtectedRoute>} />
          <Route path="/directory" element={<ProtectedRoute roles={ROLES_ADMIN_COACH_CONSULTANT}><Layout><Directory /></Layout></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute adminOnly><Layout><Settings /></Layout></ProtectedRoute>} />
          <Route path="/apply" element={<ProtectedRoute><Layout><Apply /></Layout></ProtectedRoute>} />
          <Route path="/applications" element={<ProtectedRoute adminOnly><Layout><ApplicationsAdmin /></Layout></ProtectedRoute>} />
          <Route path="/mike-chats" element={<ProtectedRoute adminOnly><Layout><MikeChats /></Layout></ProtectedRoute>} />
          <Route path="/convocations" element={<ProtectedRoute><Layout><Convocations /></Layout></ProtectedRoute>} />
          <Route path="/comunicados" element={<ProtectedRoute><Layout><Communiques /></Layout></ProtectedRoute>} />
          <Route path="/comunicado/:token" element={<PublicStudy source="communique" />} />
          <Route path="/roster" element={<ProtectedRoute><Layout><Roster /></Layout></ProtectedRoute>} />
          <Route path="/composition" element={<ProtectedRoute><Layout><Composition /></Layout></ProtectedRoute>} />
          <Route path="/salas" element={<ProtectedRoute><Layout><RoomBooking /></Layout></ProtectedRoute>} />
          <Route path="/escala" element={<ProtectedRoute><Layout><DutyRoster /></Layout></ProtectedRoute>} />
          <Route path="/reunioes-semanais" element={<ProtectedRoute><Layout><WeeklySessions /></Layout></ProtectedRoute>} />
          <Route path="/mapa-sessoes" element={<ProtectedRoute roles={["admin", "staff", "coach"]}><Layout><SessionsMap /></Layout></ProtectedRoute>} />
          <Route path="/meu-clube" element={<ProtectedRoute><MyClubRedirect /></ProtectedRoute>} />
          <Route path="/composition/:club" element={<ProtectedRoute><Layout><ClubMembers /></Layout></ProtectedRoute>} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
